import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import fetcher from "../../utils/fetcher";
import { BsWhatsapp, BsCheckAll, BsPatchCheckFill } from "react-icons/bs";
import styles from "../../styles/home.module.css";

const WA_LINK = "https://wa.me/972543255575";

// Default customer messages, shown until the admin adds testimonials from the dashboard
const DEFAULT_IN = [
  { name: "أم محمد", stars: 5, text: "اللوحة وصلت رائعة وفاقت توقعاتي! جودة ممتازة والألوان جميلة جداً" },
  { name: "سارة", stars: 5, text: "تجربة شراء سلسة وخدمة عملاء راقية. أنصح بها للجميع" },
  { name: "أبو خالد", stars: 5, text: "التغليف كان ممتاز والتوصيل سريع واللوحة ما شاء الله تبارك الرحمن" },
  { name: "نور", stars: 5, text: "بارك الله فيكم. التصميم كان بالضبط كما أردت وأكثر" },
];

// Per-position look for the four incoming messages
const IN_HUES = ["#a8825c", "#7d9f6b", "#5c8aa8", "#a85c7d"];
const IN_TIMES = ["10:42", "9:18", "11:05", "8:55"];

// Store replies stay fixed; each one follows the customer message at `after`
const OUT_REPLIES = [
  { after: 0, text: "سعدنا جداً بذوقك الرفيع، شكراً لثقتك 🌹", time: "10:45" },
  { after: 2, text: "الله يسعدك، تشرّفنا بخدمتك 🤍", time: "11:07" },
];

const buildMessages = (incoming) =>
  incoming.slice(0, 4).flatMap((t, i) => {
    const msg = {
      type: "in",
      name: t.name,
      initial: t.name?.trim().charAt(0) || "؟",
      hue: IN_HUES[i],
      stars: Math.min(Math.max(t.stars ?? 5, 1), 5),
      text: t.text,
      time: IN_TIMES[i],
    };
    const replies = OUT_REPLIES.filter((r) => r.after === i).map((r) => ({ type: "out", ...r }));
    return [msg, ...replies];
  });

const STATS = [
  { num: "+500", label: "عميل سعيد" },
  { num: "4.9", label: "متوسط التقييم" },
  { num: "98%", label: "يوصون بنا" },
];

const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  const { data } = useSWR(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials`, fetcher, {
    revalidateOnFocus: false,
    errorRetryCount: 2,
    dedupingInterval: 10000,
  });
  const MESSAGES = buildMessages(Array.isArray(data) && data.length ? data : DEFAULT_IN);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={visible ? `${styles.section} ${styles.tstVisible}` : styles.section}
    >
      <div className={styles.tstGrid}>
        {/* ---- Info column ---- */}
        <div className={styles.tstInfo}>
          <span className={styles.tstEyebrow}>
            <span className={styles.tstEyebrowStars}>★★★★★</span>
            تقييم 4.9 من 5
          </span>

          <h2 className={styles.tstHeading}>عملاؤنا يتحدثون عنّا</h2>

          <p className={styles.tstSub}>
            رسائل حقيقية تصلنا يومياً على واتساب من عملاء استلموا لوحاتهم.
            رضاكم هو أجمل ما نصنعه.
          </p>

          <div className={styles.tstStats}>
            {STATS.map((s) => (
              <div key={s.label} className={styles.tstStat}>
                <span className={styles.tstStatNum}>{s.num}</span>
                <span className={styles.tstStatLbl}>{s.label}</span>
              </div>
            ))}
          </div>

          <a href={WA_LINK} target="_blank" rel="noreferrer" className={styles.tstCta}>
            <BsWhatsapp />
            شاركنا تجربتك
          </a>

          <span className={styles.tstTrust}>
            <BsPatchCheckFill />
            تقييمات موثّقة من محادثات عملائنا
          </span>
        </div>

        {/* ---- Chat panel ---- */}
        <div className={styles.chatPanel}>
          <header className={styles.chatHeader}>
            <span className={styles.chatAvatar}>ف</span>
            <div className={styles.chatId}>
              <span className={styles.chatName}>
                فذكر
                <BsPatchCheckFill />
              </span>
              <span className={styles.chatStatus}>متصل الآن</span>
            </div>
            <BsWhatsapp className={styles.chatWa} />
          </header>

          <div className={styles.chatBody}>
            <span className={styles.dateChip}>آراء عملائنا</span>

            {MESSAGES.map((msg, i) => (
              <div
                key={i}
                className={
                  msg.type === "in"
                    ? `${styles.msgRow} ${styles.msgIn}`
                    : `${styles.msgRow} ${styles.msgOut}`
                }
                style={{ transitionDelay: `${250 + i * 130}ms` }}
              >
                {msg.type === "in" && (
                  <span className={styles.msgAvatar} style={{ background: msg.hue }}>
                    {msg.initial}
                  </span>
                )}
                <div className={msg.type === "in" ? styles.bubbleIn : styles.bubbleOut}>
                  {msg.type === "in" && (
                    <div className={styles.msgTop}>
                      <span className={styles.msgName} style={{ color: msg.hue }}>
                        {msg.name}
                      </span>
                      <span className={styles.msgStars}>{"★".repeat(msg.stars)}</span>
                    </div>
                  )}
                  <p className={styles.msgText}>{msg.text}</p>
                  <span className={styles.msgMeta}>
                    {msg.time}
                    <BsCheckAll />
                  </span>
                </div>
              </div>
            ))}

            <div
              className={`${styles.msgRow} ${styles.msgIn}`}
              style={{ transitionDelay: `${250 + MESSAGES.length * 130}ms` }}
            >
              <div className={`${styles.bubbleIn} ${styles.typing}`}>
                <span className={styles.typingDot} />
                <span className={styles.typingDot} />
                <span className={styles.typingDot} />
              </div>
            </div>
          </div>

          <footer className={styles.chatFooter}>
            <span className={styles.chatInput}>اكتب تقييمك هنا...</span>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noreferrer"
              className={styles.chatSend}
              aria-label="راسلنا على واتساب"
            >
              <BsWhatsapp />
            </a>
          </footer>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
